import React from 'react';
import Lock from "../../../../picture/lock.svg";
import Icon from '../../../jo-components/ui/icon/icon';
import { Checkbox } from 'semantic-ui-react'
import { useTranslation } from "react-i18next";
import { Button } from '../../../ui';
import Message from './message';
import formattedAmount from './utils';
import './style/resaleBreakdown.css';


function ResaleOptionToggle({ resaleOption, state, setState, disabled = false }) {
    const { t } = useTranslation();

    if (!resaleOption?.allow_private_resale) {
        return null;
    }

    return (
        <>
            <div className='wz-breakdown-title'>{t('widget.resale.breakdown.resale_options')}</div>
            <div className='wz-breakdown-content'>
                <div className='wz-breakdown-toggle'>
                    <div className='wz-breakdown-left-content'>
                        <div className='wz-breakdown-icon'>
                            <img alt='' src={Lock} />
                        </div>
                        <div>
                            <div className='wz-detail-label'>{resaleOption.label}</div>
                            <div className='wz-detail-description'>{resaleOption.description}</div>
                        </div>
                    </div>
                    <div className='wz-price-input-container'>
                        <Checkbox 
                            toggle 
                            disabled={disabled}
                            checked={state.is_private_resale} 
                            onChange={(_, { checked }) => setState({ ...state, is_private_resale: checked })} 
                        />
                    </div>
                </div>
            </div>
        </>
    );
}

function PriceBreakdown({ price, fees, total_price, total, state }) {
    return (
        <div className='wz-breakdown-content'>
            <div className='wz-price-description'>
                <div>{price.label}</div>
                <div>{formattedAmount(state.price || 0, price.currency)}</div>
            </div>
            {fees.value > 0 && (
                <div className='wz-fees-description'>
                    <div>{fees.label}</div>
                    <div>{formattedAmount(fees.value, fees.currency)}</div>
                </div>
            )}
            <div className='wz-list-item-total create'>
                <div>{total_price.label}</div>
                <div>{formattedAmount(total, total_price.currency)}</div>
            </div>
        </div>
    );
}

export default function ResaleBreakdown({ data, state, setState, mode, onSubmit }) {
    const { t } = useTranslation();
    const [confirmationStep, setConfirmationStep] = React.useState(false);

    const { resale_breakdown, resale_option: resaleOption } = data;
    const { price, fees, total_price } = resale_breakdown;
    const { min: minPrice, max: maxPrice } = price;

    const message = React.useMemo(() => {
        const confirmMessage = data['confirmation-message']?.message;
        if (!confirmMessage) return null;

        return Object.keys(confirmMessage).reduce((messageObject, key) => {
            if (!confirmMessage[key].includes('widget_template.manage_order.resale.')) {
                messageObject[key] = confirmMessage[key];
            }
            return messageObject;
        }, {});
    }, [data]);

    const inputError = React.useMemo(() => {
        if (state.price == null) return false;
        return state.price < minPrice || state.price > maxPrice;
    }, [state.price, minPrice, maxPrice]);

    const total = React.useMemo(() => {
        const value = state.price - fees.value;
        return value >= 0 ? value : 0;
    }, [state.price, fees.value]);

    const handlePriceChange = React.useCallback(({ target: { value } }) => {
        setState({ ...state, price: value });
    }, [state]);

    const handleConfirm = React.useCallback(() => {
        setConfirmationStep(true);
    }, []);

    const handleBack = React.useCallback(() => {
        setConfirmationStep(false);
    }, []);

    const _onSubmit = React.useCallback(() => {
        onSubmit({ data: state});
    }, [ onSubmit, state ]);

    // Resale Menu View
    if (mode === 'resale-menu') {
        return (
            <div className='wz-breakdown-container'>
                <div className='wz-breakdown-title'>{data.title}</div>
                <div>
                    <PriceBreakdown 
                        price={price}
                        fees={fees}
                        total_price={total_price}
                        total={total}
                        state={state}
                    />
                </div>
            </div>
        );
    }

    // Confirmation Step View
    if (confirmationStep) {
        return (
            <div className='wz-breakdown-container'>
                <div className='wz-breakdown-title'>{data.title}</div>
                <div>
                    {message && (
                        <div className='wz-breakdown-message'>
                            <Message
                                type={message.type || 'info'}
                                title={message.title || t('widget.resale.breakdown.message-info-title')}
                                description={message.body || t('widget.resale.breakdown.message-info-description')}
                            />
                        </div>
                    )}
                    <PriceBreakdown 
                        price={price}
                        fees={fees}
                        total_price={total_price}
                        total={total}
                        state={state}
                    />
                    <ResaleOptionToggle
                        resaleOption={resaleOption}
                        state={state}
                        setState={setState}
                    />
                    <div className='wz-breakdown-cta'>
                        <Button
                            className='wz-button'
                            label={t('widget.resale.breakdown.cta-confirm')}
                            onClick={_onSubmit}
                        />
                        <Button
                            className='wz-button wz-back-cta'
                            label={t('widget.resale.breakdown.cta-back')}
                            onClick={handleBack}
                        />
                    </div>
                </div>
            </div>
        );
    }

    // Edit Mode View
    return (
        <div className='wz-breakdown-container'>
            <div className='wz-breakdown-title'>{data.title}</div>
            <div className='wz-breakdown-content'>
                <div className='wz-breakdown-optional'>
                    <div className='wz-breakdown-left-content'>
                        <div className='wz-breakdown-icon'>
                            <Icon name='transfer' />
                        </div>
                        <div>
                            <div className='wz-breakdown-price-label'>{price.label}</div>
                            {minPrice !== maxPrice && (
                                <div className='wz-detail-description'>{price.description}</div>
                            )}
                        </div>
                    </div>
                    <div>
                        <div className='wz-price-input-container'>
                            <input
                                placeholder={minPrice}
                                type='number'
                                disabled={minPrice === maxPrice}
                                min={minPrice}
                                value={state.price}
                                onChange={handlePriceChange}
                                className={`wz-price-input ${inputError ? 'wz-price-input-error' : ''}`}
                            />
                            <div className='wz-price-icon'>
                                {formattedAmount(null, price.currency)}
                            </div>
                        </div>
                        {inputError && (
                            <div className='wz-input-error'>
                                {t('widget.resale.breakdown.input-error', { 
                                    min: formattedAmount(minPrice, price.currency), 
                                    max: formattedAmount(maxPrice, price.currency) 
                                })}
                            </div>
                        )}
                    </div>
                </div>
                {fees.value > 0 && (
                    <div className='wz-fees-description'>
                        <div>{fees.label}</div>
                        <div>{formattedAmount(fees.value, fees.currency)}</div>
                    </div>
                )}
                <div className='wz-list-item-total create'>
                    <div>{total_price.label}</div>
                    <div>{formattedAmount(total, total_price.currency)}</div>
                </div>
            </div>
            <ResaleOptionToggle 
                resaleOption={resaleOption}
                state={state}
                setState={setState}
            />
            <div className='wz-breakdown-cta'>
                <Button
                    className='wz-button'
                    label={t('widget.resale.breakdown.cta-confirm')}
                    onClick={handleConfirm}
                    disabled={!state.price || inputError}
                />
            </div>
        </div>
    );
}